import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState } from "react";
import { Printer, ChevronDown, ChevronUp, Dumbbell, AlertTriangle, Info, ExternalLink } from "lucide-react";
import { Link } from "wouter";

interface ExerciseLeaflet {
  id: string;
  condition: string;
  shortName: string;
  intro: string;
  exercises: {
    name: string;
    description: string;
    reps: string;
    frequency: string;
    warning?: string;
  }[];
  tips: string[];
  stopIfMessage: string;
}

const LEAFLETS: ExerciseLeaflet[] = [
  {
    id: "axspa",
    condition: "Axial Spondyloarthritis (axSpA / Ankylosing Spondylitis)",
    shortName: "axSpA",
    intro:
      "Exercise is one of the most effective ways to manage axSpA. Daily gentle movement helps reduce pain and stiffness, especially in the morning. Try to do these exercises every day.",
    exercises: [
      {
        name: "Chin Tuck",
        description: "Sit or stand tall. Gently draw your chin straight back, making a 'double chin'. Hold for 3–5 seconds, then relax.",
        reps: "10 repetitions",
        frequency: "2–3 times daily",
      },
      {
        name: "Thoracic Extension over Chair",
        description: "Sit in a chair, place your hands behind your head. Lean back gently over the top of the chair back to extend your upper spine. Hold for 5 seconds.",
        reps: "5–8 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Hip Flexor Stretch",
        description: "Kneel on one knee (use a cushion for comfort). Gently push your hips forward until you feel a stretch in the front of the kneeling leg. Hold for 20–30 seconds. Repeat both sides.",
        reps: "3 holds each side",
        frequency: "Once daily",
      },
      {
        name: "Deep Breathing",
        description: "Sit tall and breathe in deeply through your nose, expanding your chest as wide as possible. Hold for 2 seconds, then breathe out slowly. This helps maintain chest expansion.",
        reps: "5–10 breaths",
        frequency: "2 times daily",
      },
      {
        name: "Prone Lying",
        description: "Lie on your front (prone) on a firm surface or bed. Relax completely. This gentle position helps prevent forward rounding of the spine.",
        reps: "20–30 minutes",
        frequency: "Once daily (e.g. after breakfast)",
      },
    ],
    tips: [
      "Morning stiffness is common — gentle exercises in bed before you get up can help.",
      "Warm showers before exercise may ease stiffness.",
      "Swimming is an excellent low-impact exercise for axSpA.",
      "Try to sit and stand as tall as possible during the day.",
    ],
    stopIfMessage:
      "Stop and contact your physiotherapist or rheumatology team if you develop new or worsening pain in your arms or legs, or any numbness or tingling.",
  },
  {
    id: "ra",
    condition: "Rheumatoid Arthritis (RA)",
    shortName: "RA",
    intro:
      "Gentle exercise helps keep your joints mobile and your muscles strong. It is safe to exercise during RA — in fact, it is one of the best things you can do. Listen to your body and rest during severe flares.",
    exercises: [
      {
        name: "Finger Flexion & Extension",
        description: "Gently make a fist, squeezing lightly, then open your hand and spread your fingers as wide as comfortable. Do not force beyond pain.",
        reps: "10 repetitions each hand",
        frequency: "2–3 times daily",
      },
      {
        name: "Wrist Circles",
        description: "With your elbow supported on a table, slowly rotate your wrist in a circle, 5 times clockwise and 5 times anti-clockwise. Keep the movement gentle.",
        reps: "5 circles each direction",
        frequency: "2 times daily",
      },
      {
        name: "Seated Knee Straightening",
        description: "Sit in a chair. Slowly straighten one knee, holding your leg straight for 5 seconds. Lower it slowly. Alternate legs.",
        reps: "10 repetitions each leg",
        frequency: "Once daily",
      },
      {
        name: "Ankle Pumps",
        description: "Sitting or lying, flex your foot up and down (pump your ankle) and circle it gently. This helps circulation and reduces stiffness.",
        reps: "10 pumps + 5 circles each foot",
        frequency: "Several times daily",
      },
      {
        name: "Gentle Walking",
        description: "A 10–20 minute walk at a comfortable pace. Build up gradually. Supportive footwear is important.",
        reps: "10–20 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "Avoid high-impact activities (running, jumping) during a flare.",
      "Warm up gently before exercising and cool down afterwards.",
      "Hydrotherapy (warm water exercise) can be very helpful.",
      "If joints feel hot and swollen, rest them but move other joints gently.",
    ],
    stopIfMessage:
      "Stop any exercise that causes significant joint pain, swelling, or heat. Contact your rheumatology team if a joint is significantly more swollen or painful than usual.",
  },
  {
    id: "oa",
    condition: "Osteoarthritis (OA) — Knee & Hip",
    shortName: "OA (Knee & Hip)",
    intro:
      "Exercise is proven to reduce pain and improve function in osteoarthritis. Strengthening the muscles around your joints takes pressure off the joint itself. Aim to build up gradually.",
    exercises: [
      {
        name: "Seated Leg Raises",
        description: "Sit in a firm chair. Straighten one knee and lift your leg so it is level with your hip. Hold for 5 seconds, lower slowly. Repeat with the other leg.",
        reps: "10–15 repetitions each leg",
        frequency: "Once daily",
      },
      {
        name: "Standing Hip Abduction",
        description: "Stand holding a wall or chair for balance. Slowly lift one leg out to the side (keeping your knee and foot pointing forward). Hold for 2 seconds, lower slowly.",
        reps: "10 repetitions each leg",
        frequency: "Once daily",
        warning: "Only raise as high as is comfortable — do not lean to the side.",
      },
      {
        name: "Mini Squats",
        description: "Stand with feet shoulder-width apart, holding a surface for balance. Bend your knees slightly (10–20°) as if sitting back, then return to standing.",
        reps: "10 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Calf Raises",
        description: "Stand holding a surface. Rise slowly onto your tiptoes and lower back down. This strengthens calves and improves balance.",
        reps: "10–15 repetitions",
        frequency: "Once daily",
      },
      {
        name: "Walking",
        description: "Regular walking on flat ground with supportive shoes. Start with 10 minutes and build to 30 minutes most days.",
        reps: "10–30 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "Losing even a small amount of weight significantly reduces the load on knee and hip joints.",
      "Cycling (stationary bike or regular) is excellent for OA as it is low impact.",
      "Pace yourself — some mild muscle ache after exercise is normal, but joint pain lasting more than 2 hours afterwards means you overdid it.",
      "Use a walking aid if recommended by your physiotherapist.",
    ],
    stopIfMessage:
      "Stop if you experience sharp joint pain, giving way, or significant swelling. Contact your physiotherapist if these occur.",
  },
  {
    id: "gout",
    condition: "Gout",
    shortName: "Gout",
    intro:
      "During a gout attack, rest the affected joint and elevate it if possible. Once the flare has settled, gentle exercise and a healthy lifestyle help prevent future attacks.",
    exercises: [
      {
        name: "Ankle Range of Motion (between attacks)",
        description: "Sitting, gently rotate your ankle in slow circles, and flex/extend the foot. This helps maintain joint mobility between attacks.",
        reps: "10 circles each direction",
        frequency: "Once daily (only when pain-free)",
      },
      {
        name: "Toe Curls",
        description: "Sitting, curl your toes downwards and hold for 5 seconds, then spread them wide. This maintains toe joint mobility.",
        reps: "10 repetitions",
        frequency: "Once daily (only when pain-free)",
      },
      {
        name: "Walking (between attacks)",
        description: "Regular low-impact walking in supportive, well-fitting footwear. Start gently and build up.",
        reps: "15–30 minutes",
        frequency: "Most days",
      },
    ],
    tips: [
      "During an attack: rest, elevate the joint, use ice packs wrapped in a cloth for 20 minutes, and take your prescribed medication.",
      "Stay well hydrated — drink at least 2 litres of water per day.",
      "Reduce alcohol (especially beer and spirits) and high-purine foods (red meat, shellfish, offal).",
      "Maintain a healthy weight to reduce uric acid levels.",
    ],
    stopIfMessage:
      "Do not exercise the affected joint during an active gout attack. Wait until the inflammation has completely settled before resuming exercises.",
  },
];

function LeafletCard({ leaflet }: { leaflet: ExerciseLeaflet }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-emerald-100 rounded-xl p-2.5">
            <Dumbbell className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{leaflet.condition}</p>
            <p className="text-sm text-slate-500 mt-0.5">{leaflet.exercises.length} exercises</p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-6 pb-6 pt-5 space-y-6">
          <p className="text-sm text-slate-700 leading-relaxed">{leaflet.intro}</p>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Exercises</h3>
            {leaflet.exercises.map((ex, i) => (
              <div key={i} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-slate-900 text-sm">{ex.name}</p>
                  <div className="flex gap-1.5 shrink-0">
                    <span className="text-xs bg-emerald-200 text-emerald-800 rounded-full px-2 py-0.5">{ex.reps}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{ex.frequency}</span>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{ex.description}</p>
                {ex.warning && (
                  <div className="flex items-start gap-1.5 mt-2 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{ex.warning}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {leaflet.tips.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-900">
                <Info className="h-4 w-4" /> Helpful tips
              </div>
              <ul className="space-y-1">
                {leaflet.tips.map((tip, i) => (
                  <li key={i} className="text-sm text-blue-800 flex gap-2">
                    <span className="shrink-0 text-blue-400">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-start gap-2 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
              <p><strong>When to stop:</strong> {leaflet.stopIfMessage}</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:bg-emerald-50 rounded-lg px-4 py-2 transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print this leaflet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PhysioPatientLeaflets() {
  return (
    <PhysioLayout title="Patient Exercise Leaflets">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="bg-emerald-100 p-3 rounded-2xl shrink-0">
            <Dumbbell className="h-7 w-7 text-emerald-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Patient Exercise Leaflets</h2>
            <p className="text-slate-500 text-sm mt-1">
              Plain-language exercise handouts for patients with rheumatic conditions. Expand any condition to view the exercises, then print the leaflet to give to your patient.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 flex items-start gap-3 text-sm text-amber-800">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
          <p>
            These leaflets are intended as a starting point. Always tailor exercise prescription to the individual patient's needs, current disease activity, comorbidities, and medications.
          </p>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ExternalLink className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">Patient-facing version available</p>
              <p className="text-sm text-emerald-700 mt-0.5">
                Share the patient portal exercise guide with your patients — same exercises, written in plain language.
              </p>
            </div>
          </div>
          <Link
            href="/patient/exercises"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors"
          >
            View patient guide
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="space-y-4">
          {LEAFLETS.map(leaflet => (
            <LeafletCard key={leaflet.id} leaflet={leaflet} />
          ))}
        </div>
      </div>
    </PhysioLayout>
  );
}
